import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * When true, the modal can only be dismissed by clicking Acknowledge —
   * backdrop click and Escape are disabled. Used for the first-visit gate.
   */
  requireAcknowledge?: boolean;
}

export function ConfidentialityModal({
  open,
  onClose,
  requireAcknowledge = false,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !requireAcknowledge) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, requireAcknowledge, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
      onClick={requireAcknowledge ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confidentiality-title"
    >
      <Card
        className="relative flex max-h-[90vh] w-full max-w-2xl flex-col gap-3 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--warn))]">
              Project Martin · Internal
            </div>
            <h2
              id="confidentiality-title"
              className="mt-1 text-lg font-semibold"
            >
              Confidentiality Notice
            </h2>
          </div>
          {!requireAcknowledge && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label="Close"
              className="-mr-2 -mt-1 h-7 w-7 p-0"
            >
              ×
            </Button>
          )}
        </div>

        <div className="space-y-3 overflow-y-auto pr-1 text-xs leading-relaxed text-muted-foreground">
          <p>
            This dashboard and all data, signals, indicators, and strategy
            logic displayed within it are strictly confidential and proprietary
            to the Project Martin team. The contents — including entry
            conditions, signal thresholds, and any derived analytics — must
            not be screenshotted, recorded, shared, or used for any purpose
            other than those explicitly authorized by the project leads.
          </p>
          <p>
            Access is restricted to individuals who have been formally granted
            permission. By using this dashboard, you agree to keep all
            displayed information confidential and to take reasonable
            precautions against unauthorized disclosure.
          </p>
          <p>
            Unauthorized access, sharing, or distribution — whether
            intentional or accidental — constitutes a breach of confidentiality
            and may result in immediate revocation of access, disciplinary
            action, and/or legal consequences.
          </p>
          <p>
            If you have reached this dashboard in error, close it immediately
            and notify the project owner. Do not capture, redistribute, or
            retain any portion of its contents.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant={requireAcknowledge ? "default" : "secondary"}
            size="sm"
            onClick={onClose}
          >
            {requireAcknowledge ? "I acknowledge & agree" : "Close"}
          </Button>
        </div>
      </Card>
    </div>,
    document.body,
  );
}
